import { PdfImageUrlEntry } from './RRDCombinedInventoryPDFUtils';

export type DocumentClassificationResult = {
  classification_category: string;
  patient_account_number?: string;
  mrn?: string;
  payer_name?: string;
  display_name?: string;
  payer_address?: string;
  payer_city?: string;
  payer_state?: string;
  payer_zip?: string;
  patient_address?: string;
  patient_city?: string;
  patient_state?: string;
  patient_zip?: string;
  patient_name?: string;
  facility_name?: string;
  facility_address?: string;
  facility_city?: string;
  facility_state?: string;
  facility_zip?: string;
};

const DEFAULT_RESULT: DocumentClassificationResult = {
  classification_category: '',
};

const PROXY_URL =
  (process.env.REACT_APP_OPENAI_PROXY_URL as string | undefined)?.trim() ||
  'http://localhost:3001/chat';

function parseJsonFromText(text: string): DocumentClassificationResult {
  const trimmed = text.trim();
  if (!trimmed) return DEFAULT_RESULT;

  const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const jsonText = fencedMatch?.[1] || trimmed;

  try {
    return JSON.parse(jsonText) as DocumentClassificationResult;
  } catch {
    const objectMatch = jsonText.match(/\{[\s\S]*\}/);
    if (!objectMatch) return DEFAULT_RESULT;
    return JSON.parse(objectMatch[0]) as DocumentClassificationResult;
  }
}

export async function classifyDocument(
  pageImages: PdfImageUrlEntry[],
): Promise<DocumentClassificationResult> {
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      messages: [
        {
          role: 'system',
          content: 'Extract appeal inventory data from the provided PDF page images. Return only JSON with classification_category, patient_account_number, mrn, payer_name, display_name, payer_address, payer_city, payer_state, payer_zip, patient_address, patient_city, patient_state, patient_zip, patient_name, facility_name, facility_address, facility_city, facility_state, and facility_zip.',
        },
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: 'Classify this document and extract the requested fields. Return only a JSON object.',
            },
            ...pageImages,
          ],
        },
      ],
      temperature: 0,
      max_tokens: 1000,
    }),
  });

  if (!response.ok) {
    const details = await response.text().catch(() => '');
    throw new Error(`OpenAI proxy request failed (${response.status}): ${details}`);
  }

  const result = await response.json();
  const content = result?.choices?.[0]?.message?.content;
  return typeof content === 'string' ? parseJsonFromText(content) : DEFAULT_RESULT;
}
